import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  ArrowLeft,
  Download,
  Eye,
  Loader2,
  Rocket,
  Save
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  createDocIntelligenceTemplateFromReview,
  downloadDocument,
  generateDocIntelligenceTemplate,
  getDocIntelligenceTemplate,
  previewDocIntelligenceTemplate,
  promoteDocIntelligenceTemplate,
  updateDocIntelligenceTemplate
} from '../../services/api/implementation/impl';
import { btnGhost, btnPrimary, btnSecondary, DocIntelPanelHeader, DocIntelSkeleton } from './DocIntelUi';
import { extractPlaceholderNames } from './docIntelUtils';

const DocIntelTemplatePanel = ({
  dealId,
  reviewSessionId,
  meta,
  templateId: initialTemplateId,
  onTemplateCreated,
  onBack
}) => {
  const [templateId, setTemplateId] = useState(initialTemplateId);
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [status, setStatus] = useState('draft');
  const [linkedTemplateId, setLinkedTemplateId] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [loading, setLoading] = useState(true);
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const load = async () => {
      try {
        if (initialTemplateId) {
          const tpl = await getDocIntelligenceTemplate(initialTemplateId);
          setTemplateId(tpl.id);
          setName(tpl.name || '');
          setContent(tpl.content || '');
          setStatus(tpl.status || 'draft');
          setLinkedTemplateId(tpl.linked_template_id ?? null);
        } else if (reviewSessionId) {
          const baseName =
            (meta?.subtitle || 'Agreement template').replace(/\.[^.]+$/, '') ||
            'Agreement template';
          const tpl = await createDocIntelligenceTemplateFromReview(reviewSessionId, baseName);
          setTemplateId(tpl.id);
          setName(tpl.name || baseName);
          setContent(tpl.content || '');
          setStatus(tpl.status || 'draft');
          onTemplateCreated?.(tpl.id);
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to load template');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [initialTemplateId, reviewSessionId, meta?.subtitle, onTemplateCreated]);

  const placeholders = useMemo(() => extractPlaceholderNames(content), [content]);
  const isPromoted = status === 'active' || Boolean(linkedTemplateId);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateDocIntelligenceTemplate(templateId, { name: name.trim() || undefined, content }),
    onSuccess: (tpl) => {
      setStatus(tpl.status || status);
      toast.success('Template saved');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Save failed');
    }
  });

  const previewMutation = useMutation({
    mutationFn: () => previewDocIntelligenceTemplate(templateId, dealId),
    onSuccess: (data) => {
      setPreviewHtml(data?.html || '');
      toast.success('Preview updated');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Preview failed');
    }
  });

  const promoteMutation = useMutation({
    mutationFn: () => promoteDocIntelligenceTemplate(templateId),
    onSuccess: (result) => {
      setStatus(result?.definition?.status || 'active');
      setLinkedTemplateId(result?.definition?.linked_template_id ?? result?.template?.id ?? null);
      toast.success('Promoted to Property Documents');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Promote failed');
    }
  });

  const generateMutation = useMutation({
    mutationFn: () => generateDocIntelligenceTemplate(templateId, dealId),
    onSuccess: async (result) => {
      const docId = result?.document_id;
      if (!docId) {
        toast.success('Document generated');
        return;
      }
      try {
        const data = await downloadDocument(docId);
        const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `${name || 'document'}_${docId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('PDF downloaded');
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || 'Download failed');
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Generate failed');
    }
  });

  const isBusy =
    saveMutation.isPending ||
    previewMutation.isPending ||
    promoteMutation.isPending ||
    generateMutation.isPending;

  if (loading) {
    return <DocIntelSkeleton rows={4} />;
  }

  return (
    <div className="flex min-h-0 flex-col">
      <DocIntelPanelHeader
        title={meta?.title || 'Agreement template'}
        subtitle={meta?.subtitle}
        hint={meta?.hint}
        actions={
          <button type="button" onClick={onBack} className={btnGhost}>
            <ArrowLeft className="h-4 w-4" />
            Back to apply
          </button>
        }
      />

      <div className="grid gap-4 p-4 lg:grid-cols-2 lg:p-5">
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Template name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Content (HTML + placeholders)
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={14}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs leading-relaxed text-slate-800"
              spellCheck={false}
            />
          </div>

          {placeholders.length > 0 && (
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Placeholders
              </p>
              <div className="flex flex-wrap gap-1.5">
                {placeholders.map((ph) => (
                  <code
                    key={ph}
                    className="rounded-md bg-teal-50 px-2 py-0.5 text-[11px] text-teal-900 ring-1 ring-teal-100">
                    {`{{ ${ph} }}`}
                  </code>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex min-h-[280px] flex-col rounded-xl border border-slate-200 bg-slate-50/40">
          <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
            <p className="text-xs font-medium text-slate-600">Deal preview</p>
            <button
              type="button"
              disabled={isBusy || !templateId}
              onClick={() => previewMutation.mutate()}
              className={btnSecondary}>
              {previewMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
              Refresh preview
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-auto p-3">
            {previewHtml ? (
              <div
                className="prose prose-sm max-w-none rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-100"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            ) : (
              <p className="py-12 text-center text-sm text-slate-500">
                Run preview to see merged deal data in this template.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-3 sm:px-5">
        <p className="text-xs text-slate-500">
          Status:{' '}
          <span className="font-medium text-slate-700">
            {isPromoted ? 'Active (promoted)' : 'Draft'}
          </span>
          {isPromoted && ' · ready for PDF generation'}
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={isBusy || !templateId}
            onClick={() => saveMutation.mutate()}
            className={btnSecondary}>
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </button>
          {!isPromoted && (
            <button
              type="button"
              disabled={isBusy || !templateId}
              onClick={() => promoteMutation.mutate()}
              className={btnSecondary}>
              {promoteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Rocket className="h-4 w-4 text-teal-700" />
              )}
              Promote
            </button>
          )}
          <button
            type="button"
            disabled={isBusy || !templateId || !isPromoted}
            onClick={() => generateMutation.mutate()}
            className={btnPrimary}
            title={!isPromoted ? 'Promote template first' : undefined}>
            {generateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Generate PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocIntelTemplatePanel;
